import React, { useRef, useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { gsap } from 'gsap';

const CursorContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 9999;
  mix-blend-mode: difference;
`;

const CursorDot = styled.div<{ isHovered: boolean }>`
  position: absolute;
  width: ${props => props.isHovered ? '60px' : '20px'};
  height: ${props => props.isHovered ? '60px' : '20px'};
  border-radius: 50%;
  background: ${props => props.isHovered 
    ? 'radial-gradient(circle, rgba(0, 255, 136, 0.8), rgba(0, 255, 136, 0.2))'
    : 'radial-gradient(circle, rgba(0, 255, 136, 0.9), rgba(0, 255, 136, 0.3))'
  };
  border: 2px solid #00ff88;
  transform: translate(-50%, -50%);
  transition: all 0.2s ease;
  box-shadow: 
    0 0 20px rgba(0, 255, 136, 0.4),
    inset 0 0 10px rgba(0, 255, 136, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 4px;
    height: 4px;
    background: #00ff88;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    box-shadow: 0 0 10px #00ff88;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${props => props.isHovered ? '80px' : '40px'};
    height: ${props => props.isHovered ? '80px' : '40px'};
    border: 1px solid rgba(0, 255, 136, 0.2);
    border-radius: 50%;
    transform: translate(-50%, -50%);
    animation: pulse-ring 2s ease-out infinite;
  }
  
  @keyframes pulse-ring {
    0% {
      transform: translate(-50%, -50%) scale(0.8);
      opacity: 1;
    }
    100% {
      transform: translate(-50%, -50%) scale(1.2);
      opacity: 0;
    }
  }
`;

const CursorTrail = styled.div<{ x: number; y: number; index: number }>`
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(0, 255, 136, ${props => 0.6 - (props.index * 0.1)});
  left: ${props => props.x}px;
  top: ${props => props.y}px;
  transform: translate(-50%, -50%);
  pointer-events: none;
  transition: all 0.1s ease;
`;

const CursorRipple = styled.div<{ x: number; y: number; show: boolean }>`
  position: absolute;
  left: ${props => props.x}px;
  top: ${props => props.y}px;
  width: 100px;
  height: 100px;
  border: 2px solid rgba(0, 255, 136, 0.6);
  border-radius: 50%;
  transform: translate(-50%, -50%) scale(${props => props.show ? '1' : '0'});
  opacity: ${props => props.show ? '0' : '1'};
  transition: all 0.6s ease;
  pointer-events: none;
`;

interface TrailPoint {
  x: number;
  y: number;
  id: number;
}

const CustomCursor: React.FC = () => {
  const cursorRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [trailPoints, setTrailPoints] = useState<TrailPoint[]>([]);
  const [clickRipples, setClickRipples] = useState<{x: number, y: number, id: number, show: boolean}[]>([]);
  const trailId = useRef(0);
  const rippleId = useRef(0);
  const mousePos = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mousePos.current = { x: e.clientX, y: e.clientY };

      // Update trail with slight delay for smooth effect
      setTrailPoints(prev => {
        const newTrail = [{ x: e.clientX, y: e.clientY, id: trailId.current++ }, ...prev.slice(0, 5)];
        return newTrail;
      });

      // Move cursor with GSAP for smooth animation
      if (cursorRef.current) {
        gsap.to(cursorRef.current, {
          x: e.clientX,
          y: e.clientY,
          duration: 0.1,
          ease: "power2.out",
          overwrite: "auto" // Prevent conflicting animations
        });
      }
    };

    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => setIsHovered(false);

    const handleClick = (e: MouseEvent) => {
      const newRipple = {
        x: mousePos.current.x,
        y: mousePos.current.y,
        id: rippleId.current++,
        show: false
      };
      
      setClickRipples(prev => [...prev, newRipple]);
      
      // Trigger ripple animation
      setTimeout(() => {
        setClickRipples(prev => 
          prev.map(ripple => 
            ripple.id === newRipple.id 
              ? { ...ripple, show: true }
              : ripple
          )
        );
      }, 10);

      // Remove ripple after animation
      setTimeout(() => {
        setClickRipples(prev => prev.filter(ripple => ripple.id !== newRipple.id));
      }, 700);
    };

    // Add event listeners
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('click', handleClick);

    // Better hover detection using event delegation
    const handleDocumentMouseOver = (e: MouseEvent) => {
      const target = e.target as Element;
      const isInteractive = target.matches('button, a, [role="button"], .navigation-dot, .close-btn, .hidden-element, input, select, textarea, [tabindex], .nav-button, .read-button, .achievement, .bust-container') || 
                           target.closest('button, a, [role="button"], .navigation-dot, .close-btn, .hidden-element, .nav-button, .read-button, .achievement, .bust-container');
      
      setIsHovered(!!isInteractive);
    };

    const handleDocumentMouseOut = (e: MouseEvent) => {
      const target = e.target as Element;
      const relatedTarget = e.relatedTarget as Element;
      
      // Check if we're leaving an interactive element
      const leavingInteractive = target.matches('button, a, [role="button"], .navigation-dot, .close-btn, .hidden-element, input, select, textarea, [tabindex], .nav-button, .read-button, .achievement, .bust-container') || 
                                target.closest('button, a, [role="button"], .navigation-dot, .close-btn, .hidden-element, .nav-button, .read-button, .achievement, .bust-container');
      
      // Check if we're entering another interactive element
      const enteringInteractive = relatedTarget && (
        relatedTarget.matches('button, a, [role="button"], .navigation-dot, .close-btn, .hidden-element, input, select, textarea, [tabindex], .nav-button, .read-button, .achievement, .bust-container') || 
        relatedTarget.closest('button, a, [role="button"], .navigation-dot, .close-btn, .hidden-element, .nav-button, .read-button, .achievement, .bust-container')
      );
      
      // Only set to false if we're leaving an interactive element and not entering another one
      if (leavingInteractive && !enteringInteractive) {
        setIsHovered(false);
      }
    };

    document.addEventListener('mouseover', handleDocumentMouseOver);
    document.addEventListener('mouseout', handleDocumentMouseOut);

    // Hide default cursor
    document.body.style.cursor = 'none';

    // Set initial cursor position to current mouse position or center of screen
    if (cursorRef.current) {
      const initialX = mousePos.current.x || window.innerWidth / 2;
      const initialY = mousePos.current.y || window.innerHeight / 2;
      gsap.set(cursorRef.current, { x: initialX, y: initialY });
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('click', handleClick);
      document.removeEventListener('mouseover', handleDocumentMouseOver);
      document.removeEventListener('mouseout', handleDocumentMouseOut);
      document.body.style.cursor = 'auto';
    };
  }, []);

  return (
    <CursorContainer>
      {/* Cursor trail */}
      {trailPoints.map((point, index) => (
        <CursorTrail
          key={point.id}
          x={point.x}
          y={point.y}
          index={index}
        />
      ))}

      {/* Main cursor */}
      <CursorDot
        ref={cursorRef}
        isHovered={isHovered}
        style={{ 
          left: 0,
          top: 0
        }}
      />

      {/* Click ripples */}
      {clickRipples.map(ripple => (
        <CursorRipple
          key={ripple.id}
          x={ripple.x}
          y={ripple.y}
          show={ripple.show}
        />
      ))}
    </CursorContainer>
  );
};

export default CustomCursor;